import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSubscriptionProductCheckout } from '@/hooks/subscription-products';
import { Check, ShoppingCart, Gift, Sparkles, Users, Palette, HardDrive, Code, Building } from 'lucide-react';
import type { SubscriptionProduct } from '@shared/schema';

interface SubscriptionProductCardProps {
  product: SubscriptionProduct;
  className?: string;
  'data-testid'?: string;
}

const GIFT_CARD_AMOUNTS = [10, 25, 50, 75, 100, 150, 200, 250];

const CATEGORY_COLORS: Record<string, string> = {
  premium: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white',
  character_studio: 'bg-gradient-to-r from-purple-600 to-pink-500 text-white',
  child_profiles: 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white',
  illustrations: 'bg-gradient-to-r from-rose-500 to-pink-500 text-white',
  storage: 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white',
  gift_cards: 'bg-gradient-to-r from-green-500 to-lime-500 text-white',
  white_label: 'bg-gradient-to-r from-slate-600 to-gray-700 text-white',
  api: 'bg-gradient-to-r from-indigo-500 to-blue-600 text-white',
};

const CATEGORY_ICONS: Record<string, typeof Sparkles> = {
  premium: Sparkles,
  character_studio: Palette,
  child_profiles: Users,
  illustrations: Palette,
  storage: HardDrive,
  gift_cards: Gift,
  white_label: Building,
  api: Code,
};

function formatCategoryLabel(category: string): string {
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatInterval(interval: string): string {
  switch (interval) {
    case 'monthly':
      return '/month';
    case 'annual':
      return '/year';
    case 'one_time':
      return ' one-time';
    case 'lifetime':
      return ' lifetime';
    default:
      return '';
  }
}

function truncateDescription(description: string, maxLength: number = 120): string {
  if (description.length <= maxLength) return description;
  return description.slice(0, maxLength).trim() + '...';
}

export function SubscriptionProductCard({ 
  product, 
  className = '',
  'data-testid': testId = 'subscription-product-card'
}: SubscriptionProductCardProps) {
  const metadata = product.metadata || {};
  const isGiftCard = metadata.category === 'gift_cards';
  const [selectedGiftAmount, setSelectedGiftAmount] = useState<number>(GIFT_CARD_AMOUNTS[1]);
  
  const checkout = useSubscriptionProductCheckout();

  const handlePurchase = () => {
    const request: { productId: number; giftCardAmount?: number } = {
      productId: product.id,
    };
    
    if (isGiftCard) {
      request.giftCardAmount = selectedGiftAmount;
    }
    
    checkout.mutate(request);
  };

  const price = parseFloat(product.price);
  const displayPrice = isGiftCard ? selectedGiftAmount : price;
  const category = metadata.category || 'premium';
  const CategoryIcon = CATEGORY_ICONS[category] || Sparkles;
  const features = product.features || [];

  return (
    <Card 
      className={`transition-all duration-200 hover:shadow-lg hover:border-purple-200 dark:hover:border-purple-800 overflow-hidden ${className}`}
      data-testid={testId}
    >
      {metadata.imageUrl && (
        <div className="relative w-full h-40 overflow-hidden">
          <img
            src={metadata.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover"
            data-testid={`${testId}-image`}
          />
          <div className="absolute top-2 right-2">
            <Badge 
              className={`${CATEGORY_COLORS[category] || CATEGORY_COLORS.premium}`}
              data-testid={`${testId}-category-badge`}
            >
              <CategoryIcon className="h-3 w-3 mr-1" />
              {formatCategoryLabel(category)}
            </Badge>
          </div>
        </div>
      )}
      
      <CardHeader className={metadata.imageUrl ? 'pt-4' : ''}>
        <CardTitle className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {!metadata.imageUrl && <CategoryIcon className="h-5 w-5 text-purple-600" />}
            <span className="text-lg font-semibold" data-testid={`${testId}-name`}>
              {product.name}
            </span>
          </div>
          {!metadata.imageUrl && (
            <Badge 
              className={`${CATEGORY_COLORS[category] || CATEGORY_COLORS.premium}`}
              data-testid={`${testId}-category-badge`}
            >
              {formatCategoryLabel(category)}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <p 
          className="text-sm text-muted-foreground" 
          data-testid={`${testId}-description`}
          title={product.description}
        >
          {truncateDescription(product.description)}
        </p>

        {isGiftCard && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Amount</label>
            <Select
              value={selectedGiftAmount.toString()}
              onValueChange={(value) => setSelectedGiftAmount(parseInt(value))}
              data-testid={`${testId}-gift-amount-select`}
            >
              <SelectTrigger data-testid={`${testId}-gift-amount-trigger`}>
                <SelectValue placeholder="Select gift card amount" />
              </SelectTrigger>
              <SelectContent>
                {GIFT_CARD_AMOUNTS.map((amount) => (
                  <SelectItem 
                    key={amount} 
                    value={amount.toString()}
                    data-testid={`${testId}-gift-amount-option-${amount}`}
                  >
                    ${amount.toFixed(2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {features.length > 0 && (
          <ul className="space-y-2" data-testid={`${testId}-features`}>
            {features.map((feature, index) => (
              <li 
                key={index} 
                className="flex items-start gap-2 text-sm"
                data-testid={`${testId}-feature-${index}`}
              >
                <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="bg-muted rounded-lg p-3">
          <div className="flex justify-between items-center">
            <span className="font-medium">Price:</span>
            <div className="text-right">
              <span 
                className="text-xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent"
                data-testid={`${testId}-price`}
              >
                ${displayPrice.toFixed(2)}
              </span>
              {!isGiftCard && (
                <span 
                  className="text-sm text-muted-foreground ml-1"
                  data-testid={`${testId}-interval`}
                >
                  {formatInterval(product.interval)}
                </span>
              )}
            </div>
          </div>
        </div>

        <Button
          onClick={handlePurchase}
          disabled={checkout.isPending || !product.isActive}
          className="w-full bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700"
          size="lg"
          data-testid={`${testId}-purchase-button`}
        >
          {checkout.isPending ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              Processing...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {isGiftCard ? (
                <>
                  <Gift className="h-4 w-4" />
                  Buy Gift Card - ${displayPrice.toFixed(2)}
                </>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4" />
                  {product.interval === 'one_time' || product.interval === 'lifetime' 
                    ? `Buy Now - $${displayPrice.toFixed(2)}`
                    : `Subscribe - $${displayPrice.toFixed(2)}${formatInterval(product.interval)}`
                  }
                </>
              )}
            </div>
          )}
        </Button>

        {!product.isActive && (
          <p className="text-sm text-muted-foreground text-center" data-testid={`${testId}-unavailable`}>
            This product is currently unavailable
          </p>
        )}
      </CardContent>
    </Card>
  );
}
